<script setup>
	import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";
	import { executeServiceCall, getValidationErrorsFromResponse, selfServiceService } from "@/services";
	import TestimonialCarousel from "@/components/TestimonialCarousel.vue";

	const testimonials = [
		{
			author: "Geoff A",
			quote: "Chad is very talented technically, and he brings a wealth of experience and ability to the projects he's involved in. He also has a lot to offer in terms of project management — he is an effective communicator and has experience with a variety of software development methodologies. Finally, and most importantly, Chad is a phenomenal mentor and teacher.",
		},
		{
			author: "Joshua L",
			quote: "Chad is one of those rare individuals whose vision extends beyond just the project at hand. He sees the big picture, both technically, managerially, and in relationships. He is very talented when it comes to technical matters, and also knows how to train and mentor individuals to help them reach their maximum potential.",
		},
		{
			author: "Raul G",
			quote: "We hired Chad for a quick turnaround (3 weeks!) SaaS project and he delivered, exceptionally. Our team and, more importantly, the customers felt the demo prototype was professionally done and exceeded expectations. Chad was extremely responsive, knowledgeable and great to work with.",
		},
		{
			author: "Raj S",
			quote: "In a matter of weeks Chad assumed responsibility for the majority of the user interface. Throughout the project Chad offered fresh options and new ideas allowing us to leverage the latest technology capabilities. In addition to development, Chad took a leadership role in defining our build process and policies.",
		},
		{
			author: "Josh S",
			quote: "Chad is easily the smartest and most innovative architect and engineer I have worked with. Rarely do you come across someone with his breadth and command of technical knowledge. He fundamentally understands how to build robust systems that provide value to his clients. He is also a great communicator.",
		},
		{
			author: "Larry M",
			quote: "Chad is a remarkable architect and developer. He was able to deliver on the project, despite the technical and management challenges. With constant change in technical direction, he was able to adapt well. I hope to work with Chad again in the future.",
		},
		{
			author: "AD A",
			quote: "We hired Chad to help us migrate a number of PHP-based applications to .NET as part of a migration off an old intranet. Without existing documentation, Chad was able to work with business users and the existing code base to re-write three applications from the ground up in a matter of weeks.",
		},
		{
			author: "DJ W",
			quote: "Chad Greer is one of the top Microsoft Software Solution Consultants in the market. He has great depth in architecting and developing custom solutions using an Agile approach to solve complex business problems. He has a natural ability to apply his past experiences to new challenges.",
		},
		{
			author: "Tor N",
			quote: "Chad is a technically gifted individual, with an eye for detail, and operates well as part of a team in respect of innovative and creative approaches to design, development and implementation. He has an impeccable work ethic and is one of the most innovative software architects I've known.",
		},
		{
			author: "Matt B",
			quote: "Chad was my counterpart in defining and developing our company's software, including the database, data operations, the presentation software and the overarching architecture. He understands how to write well-formed and maintainable software, but he was a shining star because he understood how to craft an enterprise solution.",
		},
		{
			author: "Chad S",
			quote: "Chad is without a doubt one of the most innovative software architects I've known and a very powerful technical leader. He has a talent for inspiring passion in others. He is very intelligent, has an impeccable work ethic and does his homework. I highly recommend Chad Greer for any technical leadership position.",
		},
	];

	const capabilities = [
		{
			command: "audit",
			title: "Diagnose the chaos",
			description: "We read what the AI actually wrote, map the landmines, and tell you in plain English what's holding the whole thing together.",
		},
		{
			command: "refactor",
			title: "Stabilize the build",
			description: "Clean, tested, maintainable code that stops breaking every time you breathe on it. Written by humans who have shipped real systems.",
		},
		{
			command: "ship",
			title: "Make it last",
			description: "Architecture that scales with you instead of collapsing under you. No duct tape, no mystery dependencies, no 3am surprises.",
		},
	];

	const editorViews = [
		{
			fileName: "App.vue",
			headline: "we make sense of the chaos.",
			lines: [
				{ tokens: [{ text: "<!-- Your AI-generated app: -->", type: "comment" }] },
				{ tokens: [{ text: "<template>", type: "punc" }] },
				{ indentLevel: 1, tokens: [{ text: "<div ", type: "punc" }, { text: "v-if", type: "keyword" }, { text: "=", type: "punc" }, { text: "\"itWorks\"", type: "string" }, { text: ">{{ ", type: "punc" }, { text: "maybe", type: "plain" }, { text: " }}</div>", type: "punc" }, { text: " // nobody's sure", type: "warn" }] },
				{ tokens: [{ text: "</template>", type: "punc" }] },
				{ tokens: [{ text: "// 800 lines, untyped, undocumented.", type: "comment" }] },
			],
		},
		{
			fileName: "fix-my-app.ts",
			headline: "let the experts clean it up.",
			lines: [
				{ tokens: [{ text: "// Do you have a vibe-coded mess?", type: "comment" }] },
				{ tokens: [{ text: "function ", type: "keyword" }, { text: "fixMyApp", type: "fn" }, { text: "() {", type: "punc" }] },
				{ indentLevel: 1, tokens: [{ text: "const ", type: "keyword" }, { text: "mess ", type: "plain" }, { text: "= ", type: "punc" }, { text: "inheritedFromAI", type: "fn" }, { text: "();", type: "punc" }, { text: " // nobody knows how this works", type: "warn" }] },
				{ indentLevel: 1, tokens: [{ text: "return ", type: "keyword" }, { text: "experts", type: "plain" }, { text: ".", type: "punc" }, { text: "cleanUp", type: "fn" }, { text: "(", type: "punc" }, { text: "mess", type: "plain" }, { text: ");", type: "punc" }] },
				{ tokens: [{ text: "}", type: "punc" }] },
			],
		},
		{
			fileName: "tangled.js",
			headline: "we untangle it so you don't have to.",
			lines: [
				{ tokens: [{ text: "// What the AI actually shipped:", type: "comment" }] },
				{ tokens: [{ text: "const ", type: "keyword" }, { text: "app ", type: "plain" }, { text: "= ", type: "punc" }, { text: "magic", type: "fn" }, { text: "();", type: "punc" }, { text: " // undocumented, untyped", type: "warn" }] },
				{ tokens: [{ text: "app", type: "plain" }, { text: ".", type: "punc" }, { text: "then", type: "fn" }, { text: "(", type: "punc" }, { text: "doStuff", type: "plain" }, { text: ").", type: "punc" }, { text: "catch", type: "fn" }, { text: "(() => {});", type: "punc" }, { text: " // errors? swallowed", type: "warn" }] },
				{ tokens: [{ text: "while ", type: "keyword" }, { text: "(", type: "punc" }, { text: "bugs", type: "plain" }, { text: ".length) ", type: "punc" }, { text: "patch", type: "fn" }, { text: "(", type: "punc" }, { text: "bugs", type: "plain" }, { text: ".", type: "punc" }, { text: "pop", type: "fn" }, { text: "());", type: "punc" }] },
				{ tokens: [{ text: "// shipped on a Friday, of course.", type: "comment" }] },
			],
		},
	];

	const heroBadges = [
		{ id: "no-bs", label: "No B.S." },
		{ id: "human-written", label: "Human-written" },
	];

	const data = reactive({
		requestMoreInfo: {
			contactInfo: {
				emailAddress: "",
				name: "",
				text: "",
			},
			isError: false,
			isProcessing: false,
			isShowing: false,
			isSuccess: false,
			topic: "",
			validationErrors: [],
		},
		typedHeadline: "",
	});

	const activeViewIndex = ref(1);
	const celebratingBadgeId = ref(null);

	const activeView = computed(() => editorViews[activeViewIndex.value]);
	const lineNumberCount = computed(() => activeView.value.lines.length + 2);

	const typingSpeedMilliseconds = 55;
	const replayPeriodMilliseconds = 10000;
	const badgeCelebrationMilliseconds = 1400;

	let typingInterval = null;
	let replayTimer = null;
	let badgeCelebrationTimer = null;

	const requestMoreInfoContent = {
		General: {
			title: "Talk through your situation",
			description: "Tell us a little about what's going on and we'll get back to you shortly.",
		},
		GetStarted: {
			title: "Let's get started",
			description: "Tell us a bit about your project and we'll reach out to get things moving.",
		},
	};

	const activeRequestMoreInfoContent = computed(() => requestMoreInfoContent[data.requestMoreInfo.topic] || requestMoreInfoContent.General);

	function requestMoreInfo(topic) {
		data.requestMoreInfo.topic = topic;
		data.requestMoreInfo.isError = false;
		data.requestMoreInfo.isSuccess = false;
		data.requestMoreInfo.isProcessing = false;
		data.requestMoreInfo.validationErrors = [];
		data.requestMoreInfo.isShowing = true;
	}

	function closeRequestMoreInfo() {
		data.requestMoreInfo.isShowing = false;
	}

	async function submitRequestForMoreInfo() {
		data.requestMoreInfo.isProcessing = true;
		data.requestMoreInfo.isError = false;
		data.requestMoreInfo.validationErrors = [];
		data.requestMoreInfo.contactInfo.phoneNumber = "n/a";
		data.requestMoreInfo.contactInfo.recaptchaResponse = "skip";

		await executeServiceCall(() => selfServiceService.public.submitContactRequest(data.requestMoreInfo.contactInfo))
			.then(() => {
				data.requestMoreInfo.isSuccess = true;
			})
			.catch(error => {
				if (error?.response?.status === 400) {
					data.requestMoreInfo.validationErrors = getValidationErrorsFromResponse(error.response).map(validationMessage =>
						validationMessage === "Your E-mail Address is required."
							? "Your E-mail Address or Phone Number is required."
							: validationMessage
					);
				} else {
					console.error(error);
					data.requestMoreInfo.isError = true;
				}
			})
			.finally(() => {
				data.requestMoreInfo.isProcessing = false;
			});
	}

	function handleModalKeydown(event) {
		if (event.key === "Escape") {
			closeRequestMoreInfo();
		}
	}

	function stopTypingHeadline() {
		if (typingInterval) {
			clearInterval(typingInterval);
			typingInterval = null;
		}
	}

	function typeActiveHeadline() {
		stopTypingHeadline();

		const headline = activeView.value.headline;
		const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

		if (prefersReducedMotion) {
			data.typedHeadline = headline;
			return;
		}

		data.typedHeadline = "";
		let characterIndex = 0;

		typingInterval = setInterval(() => {
			data.typedHeadline = headline.slice(0, characterIndex + 1);
			characterIndex += 1;

			if (characterIndex >= headline.length) {
				stopTypingHeadline();
			}
		}, typingSpeedMilliseconds);
	}

	function startReplayTimer() {
		if (replayTimer) {
			clearInterval(replayTimer);
			replayTimer = null;
		}

		const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

		if (prefersReducedMotion) {
			return;
		}

		replayTimer = setInterval(() => {
			typeActiveHeadline();
		}, replayPeriodMilliseconds);
	}

	function selectEditorView(viewIndex) {
		activeViewIndex.value = viewIndex;
		typeActiveHeadline();
		startReplayTimer();
	}

	function celebrateBadge(badgeId) {
		if (badgeCelebrationTimer) {
			clearTimeout(badgeCelebrationTimer);
			badgeCelebrationTimer = null;
		}

		celebratingBadgeId.value = null;

		requestAnimationFrame(() => {
			celebratingBadgeId.value = badgeId;
		});

		badgeCelebrationTimer = setTimeout(() => {
			celebratingBadgeId.value = null;
		}, badgeCelebrationMilliseconds);
	}

	watch(() => data.requestMoreInfo.isShowing, (isShowing) => {
		if (isShowing) {
			const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
			document.documentElement.style.overflow = "hidden";
			document.body.style.overflow = "hidden";

			if (scrollbarWidth > 0) {
				document.body.style.paddingRight = `${scrollbarWidth}px`;
			}

			window.addEventListener("keydown", handleModalKeydown);
		} else {
			document.documentElement.style.overflow = "";
			document.body.style.overflow = "";
			document.body.style.paddingRight = "";
			window.removeEventListener("keydown", handleModalKeydown);
		}
	});

	onMounted(() => {
		typeActiveHeadline();
		startReplayTimer();
	});

	onBeforeUnmount(() => {
		stopTypingHeadline();

		if (replayTimer) {
			clearInterval(replayTimer);
		}

		if (badgeCelebrationTimer) {
			clearTimeout(badgeCelebrationTimer);
		}

		document.documentElement.style.overflow = "";
		document.body.style.overflow = "";
		document.body.style.paddingRight = "";
		window.removeEventListener("keydown", handleModalKeydown);
	});
</script>

<template>
	<div class="promotion-a">
		<section class="hero">
			<div class="hero-inner">
				<div class="editor">
					<div class="editor-tabs">
						<button
							v-for="(view, viewIndex) in editorViews"
							:key="view.fileName"
							type="button"
							class="editor-tab editor-tab-button"
							:class="{ 'editor-tab-active': viewIndex === activeViewIndex }"
							:aria-pressed="viewIndex === activeViewIndex"
							@click="selectEditorView(viewIndex)"
						>
							<span v-if="viewIndex === activeViewIndex" class="editor-tab-dot" aria-hidden="true"></span>
							{{ view.fileName }}
						</button>
						<span class="editor-path">~/your-codebase</span>
					</div>

					<div class="editor-body">
						<div class="editor-gutter" aria-hidden="true">
							<span v-for="lineNumber in lineNumberCount" :key="lineNumber">{{ lineNumber }}</span>
						</div>
						<div class="editor-code">
							<p
								v-for="(codeLine, codeLineIndex) in activeView.lines"
								:key="codeLineIndex"
								class="code-line"
								:style="codeLine.indentLevel ? { paddingLeft: codeLine.indentLevel * 2 + 'ch' } : null"
							>
								<span
									v-for="(token, tokenIndex) in codeLine.tokens"
									:key="tokenIndex"
									:class="`tok-${token.type}`"
								>{{ token.text }}</span>
							</p>
							<p class="code-line">&nbsp;</p>
							<h1 class="code-headline">
								<span class="tok-comment">&gt; </span><span class="code-headline-text">{{ data.typedHeadline }}</span><span class="code-caret" :class="{ 'code-caret-blink': data.typedHeadline.length >= activeView.headline.length }"></span>
							</h1>
						</div>
					</div>
				</div>

				<p class="hero-sub">
					AI shipped you something that sort of runs and nobody can maintain. We're the
					experienced people who quietly fix it, make it solid, and hand it back working.
				</p>

				<div class="hero-tags">
					<button
						v-for="badge in heroBadges"
						:key="badge.id"
						type="button"
						class="tag tag-button"
						:class="{ 'tag-celebrate': celebratingBadgeId === badge.id }"
						@click="celebrateBadge(badge.id)"
					>
						{{ badge.label }}
						<span class="tag-check" aria-hidden="true">
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7" /></svg>
						</span>
					</button>
					<span class="tag tag-accent">50% off your first 6 weeks</span>
				</div>

				<div class="hero-actions">
					<button type="button" class="btn btn-primary" @click="requestMoreInfo('GetStarted')">Get Started</button>
					<RouterLink :to="{ name: 'how-we-work' }" class="btn btn-ghost">How we work</RouterLink>
				</div>

				<p class="hero-note">No commitment to run the command. Just a straight answer.</p>
			</div>
		</section>

		<section class="caps">
			<div class="caps-inner">
				<header class="block-header">
					<span class="block-eyebrow">What we do</span>
					<h2 class="block-heading">Three moves out of the mess</h2>
				</header>
				<div class="caps-grid">
					<article
						v-for="capability in capabilities"
						:key="capability.command"
						class="cap-card"
					>
						<div class="cap-command"><span class="cap-command-prompt">$</span> {{ capability.command }}</div>
						<h3 class="cap-title">{{ capability.title }}</h3>
						<p class="cap-description">{{ capability.description }}</p>
						<div class="cap-status"><span class="cap-status-dot"></span> ready</div>
					</article>
				</div>
			</div>
		</section>

		<section class="cta">
			<div class="cta-inner">
				<div class="palette">
					<div class="palette-bar">
						<span class="palette-search">
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
								<circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
							</svg>
							Schedule a conversation…
						</span>
						<span class="palette-key">↵</span>
					</div>
					<div class="palette-body">
						<h2 class="cta-title">Schedule your no-B.S. conversation</h2>
						<p class="cta-sub">
							Tell us what's broken. We'll tell you straight whether software is even
							the answer and what it actually takes to fix it. No script, no fluff.
						</p>
						<div class="cta-actions">
							<button type="button" class="btn btn-primary" @click="requestMoreInfo('GetStarted')">Get Started</button>
							<button type="button" class="btn btn-ghost" @click="requestMoreInfo('General')">Talk through your situation</button>
						</div>
						<p class="cta-offer">New clients get <strong>50% off the first 6 weeks.</strong></p>
					</div>
				</div>
			</div>
		</section>

		<section class="testimonials">
			<div class="testimonials-inner">
				<header class="block-header">
					<span class="block-eyebrow">What clients say</span>
					<h2 class="block-heading">In their own words</h2>
				</header>

				<div class="testimonials-carousel">
					<TestimonialCarousel :items="testimonials">
						<template #default="{ item, itemIndex }">
							<figure class="testimonial-card">
								<div class="testimonial-meta">
									<span class="testimonial-index">{{ String(itemIndex + 1).padStart(2, "0") }}</span>
									<span class="testimonial-file">review.log</span>
								</div>
								<blockquote class="testimonial-quote">{{ item.quote }}</blockquote>
								<figcaption class="testimonial-author"><span class="testimonial-author-prompt">&gt;</span> {{ item.author }}</figcaption>
							</figure>
						</template>
					</TestimonialCarousel>
				</div>
			</div>
		</section>

		<transition name="modal-fade">
			<div v-if="data.requestMoreInfo.isShowing" class="modal-overlay" @click.self="closeRequestMoreInfo">
				<div class="modal-dialog" role="dialog" aria-modal="true" aria-labelledby="requestMoreInfoTitle" @click.stop>
					<button type="button" class="modal-close" aria-label="Close" @click="closeRequestMoreInfo"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 6l12 12M18 6L6 18" /></svg></button>
					<div class="modal-scroll">
						<template v-if="!data.requestMoreInfo.isSuccess">
							<span class="modal-badge">Free · No commitment</span>
							<h2 id="requestMoreInfoTitle" class="modal-title">{{ activeRequestMoreInfoContent.title }}</h2>
							<p class="modal-description">{{ activeRequestMoreInfoContent.description }}</p>
							<div class="modal-field">
								<label for="requestMoreInfoName">Name</label>
								<input id="requestMoreInfoName" v-model="data.requestMoreInfo.contactInfo.name" type="text" autocomplete="name" :disabled="data.requestMoreInfo.isProcessing" />
							</div>
							<div class="modal-field">
								<label for="requestMoreInfoEmail">Email or phone number</label>
								<input id="requestMoreInfoEmail" v-model="data.requestMoreInfo.contactInfo.emailAddress" type="text" autocomplete="email" :disabled="data.requestMoreInfo.isProcessing" />
							</div>
							<div class="modal-field">
								<label for="requestMoreInfoText">What's going on with your software?</label>
								<textarea id="requestMoreInfoText" v-model="data.requestMoreInfo.contactInfo.text" rows="4" :disabled="data.requestMoreInfo.isProcessing"></textarea>
							</div>
							<div v-if="data.requestMoreInfo.validationErrors.length > 0" class="modal-feedback modal-feedback-error" role="alert">
								<p class="modal-feedback-title">Please fix the following:</p>
								<ul class="modal-feedback-list">
									<li v-for="(validationError, validationErrorIndex) in data.requestMoreInfo.validationErrors" :key="validationErrorIndex">{{ validationError }}</li>
								</ul>
							</div>
							<p v-else-if="data.requestMoreInfo.isError" class="modal-feedback modal-feedback-error" role="alert">Something went wrong sending your message. Please try again.</p>
							<button type="button" class="btn btn-primary btn-block" :disabled="data.requestMoreInfo.isProcessing" @click="submitRequestForMoreInfo"><span v-if="data.requestMoreInfo.isProcessing" class="btn-spinner" aria-hidden="true"></span>{{ data.requestMoreInfo.isProcessing ? "Sending…" : "Send message" }}</button>
						</template>
						<div v-else class="modal-success">
							<span class="success-check" aria-hidden="true">
								<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7" /></svg>
							</span>
							<h2 class="modal-title">Message received</h2>
							<p class="modal-description">Thanks for reaching out — we'll be in touch with you shortly.</p>
						</div>
					</div>
				</div>
			</div>
		</transition>
	</div>
</template>

<style scoped>
	.promotion-a {
		--bg: #eef2f9;
		--surface: #ffffff;
		--gutter: #f1f5fb;
		--border: #dbe4f0;
		--border-strong: #c6d4e6;
		--ink: #1e293b;
		--ink-soft: #475569;
		--ink-muted: #8190a5;
		--blue: #2563eb;
		--blue-deep: #1d4ed8;
		--blue-soft: #dbe8fe;
		--tok-keyword: #1d4ed8;
		--tok-fn: #0e7490;
		--tok-string: #0f766e;
		--tok-comment: #64748b;
		--tok-warn: #b45309;

		font-family: "JetBrains Mono", "IBM Plex Mono", "SF Mono", ui-monospace, "Courier New", monospace;
		color: var(--ink);
		background: var(--bg);
		--carousel-accent: var(--blue);
		--carousel-dot: #c2cfe0;
		--carousel-arrow-bg: #ffffff;
		--carousel-arrow-border: rgba(37, 99, 235, 0.3);
		overflow-x: hidden;
	}

	.hero {
		padding: clamp(3.5rem, 8vw, 6.5rem) 1.5rem clamp(3rem, 6vw, 5rem);
	}

	.hero-inner {
		max-width: 920px;
		margin: 0 auto;
		text-align: center;
	}

	.editor {
		text-align: left;
		border: 1px solid var(--border);
		border-radius: 14px;
		background: var(--surface);
		overflow: hidden;
		box-shadow: 0 30px 60px -38px rgba(30, 58, 95, 0.45);
	}

	.editor-tabs {
		display: flex;
		align-items: center;
		gap: 0.4rem;
		padding: 0.55rem 0.85rem;
		background: var(--gutter);
		border-bottom: 1px solid var(--border);
	}

	.editor-tab {
		display: inline-flex;
		align-items: center;
		gap: 0.45rem;
		padding: 0.4rem 0.85rem;
		border-radius: 8px;
		font-size: 0.82rem;
		color: var(--ink-muted);
		white-space: nowrap;
	}

	.editor-tab-button {
		font-family: inherit;
		background: transparent;
		border: 1px solid transparent;
		cursor: pointer;
		transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
	}

	.editor-tab-active {
		background: var(--surface);
		color: var(--ink);
		border: 1px solid var(--border);
		font-weight: 600;
	}

	.editor-tab-dot {
		width: 8px;
		height: 8px;
		border-radius: 50%;
		background: var(--blue);
	}

	.editor-tab-button:hover {
		background: var(--surface);
		border-color: var(--border);
		color: var(--ink);
	}

	.editor-tab-button:not(.editor-tab-active):not(:hover) {
		background: rgba(37, 99, 235, 0.06);
		border-color: var(--border);
	}

	.editor-tab-button:focus-visible {
		outline: 3px solid rgba(37, 99, 235, 0.4);
		outline-offset: 2px;
	}

	.editor-path {
		margin-left: auto;
		font-size: 0.78rem;
		color: var(--ink-muted);
	}

	.editor-body {
		display: flex;
		padding: 1.25rem 0;
	}

	.editor-gutter {
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		gap: 0;
		padding: 0 1rem;
		border-right: 1px solid var(--border);
		color: var(--ink-muted);
		font-size: clamp(0.85rem, 1.3vw, 1rem);
		line-height: 1.9;
		user-select: none;
	}

	.editor-code {
		flex: 1;
		padding: 0 clamp(1rem, 3vw, 1.75rem);
		min-width: 0;
	}

	.code-line {
		font-size: clamp(0.85rem, 1.35vw, 1rem);
		line-height: 1.9;
		margin: 0;
		white-space: pre-wrap;
		word-break: break-word;
	}

	.tok-keyword { color: var(--tok-keyword); font-weight: 600; }
	.tok-fn { color: var(--tok-fn); }
	.tok-string { color: var(--tok-string); }
	.tok-punc { color: var(--ink-soft); }
	.tok-comment { color: var(--tok-comment); }
	.tok-warn { color: var(--tok-warn); }
	.tok-plain { color: var(--ink); }

	.code-headline {
		margin: 0.5rem 0 0;
		font-size: clamp(1.5rem, 4.2vw, 2.7rem);
		line-height: 1.25;
		font-weight: 700;
		letter-spacing: -0.01em;
	}

	.code-headline-text {
		color: var(--blue-deep);
		background: var(--blue-soft);
		padding: 0.05em 0.25em;
		border-radius: 6px;
		box-decoration-break: clone;
		-webkit-box-decoration-break: clone;
	}

	.code-caret {
		display: inline-block;
		width: 0.55ch;
		height: 1.05em;
		margin-left: 3px;
		background: var(--blue);
		vertical-align: text-bottom;
	}

	.code-caret-blink {
		animation: code-caret-blink 1.05s step-end infinite;
	}

	@keyframes code-caret-blink {
		0%, 100% { opacity: 1; }
		50% { opacity: 0; }
	}

	.hero-sub {
		max-width: 62ch;
		margin: 2.25rem auto 0;
		font-size: clamp(1.05rem, 1.4vw, 1.22rem);
		line-height: 1.7;
		color: var(--ink-soft);
	}

	.hero-tags {
		display: flex;
		gap: 0.6rem;
		justify-content: center;
		flex-wrap: wrap;
		margin-top: 1.85rem;
	}

	.tag {
		padding: 0.45rem 0.95rem;
		border-radius: 8px;
		background: var(--surface);
		border: 1px solid var(--border);
		color: var(--ink-soft);
		font-size: 0.9rem;
		font-weight: 600;
	}

	.tag-button {
		font-family: inherit;
		cursor: pointer;
		position: relative;
		display: inline-flex;
		align-items: center;
		transition: transform 0.2s ease, background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
	}

	.tag-button:hover {
		border-color: var(--blue);
		color: var(--blue-deep);
	}

	.tag-button:focus-visible {
		outline: 3px solid rgba(37, 99, 235, 0.4);
		outline-offset: 2px;
	}

	.tag-check {
		display: inline-flex;
		align-items: center;
		width: 0;
		margin-left: 0;
		overflow: hidden;
		opacity: 0;
		color: var(--blue-deep);
		transition: width 0.3s ease, opacity 0.3s ease, margin-left 0.3s ease;
	}

	.tag-check svg {
		width: 14px;
		height: 14px;
	}

	.tag-celebrate {
		background: var(--blue-soft);
		border-color: var(--blue);
		color: var(--blue-deep);
		animation: tag-pop 0.4s ease;
	}

	.tag-celebrate .tag-check {
		width: 14px;
		opacity: 1;
		margin-left: 0.4rem;
	}

	.tag-celebrate::after {
		content: "";
		position: absolute;
		inset: 0;
		border-radius: inherit;
		pointer-events: none;
		animation: tag-ring 0.75s ease-out forwards;
	}

	@keyframes tag-pop {
		0% { transform: scale(1); }
		45% { transform: scale(1.06); }
		100% { transform: scale(1); }
	}

	@keyframes tag-ring {
		0% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.4); }
		100% { box-shadow: 0 0 0 14px rgba(37, 99, 235, 0); }
	}

	.tag-accent {
		color: var(--blue-deep);
		border-color: var(--blue);
		background: var(--blue-soft);
	}

	.hero-actions {
		display: flex;
		gap: 1rem;
		justify-content: center;
		flex-wrap: wrap;
		margin-top: 2.25rem;
	}

	.btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 0.95rem 1.9rem;
		border-radius: 10px;
		font-family: inherit;
		font-size: 1.05rem;
		font-weight: 700;
		text-decoration: none;
		cursor: pointer;
		border: 1px solid transparent;
		transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
	}

	.btn-primary {
		background: var(--blue);
		color: #ffffff;
		box-shadow: 0 14px 28px -14px rgba(37, 99, 235, 0.7);
	}

	.btn-primary:hover {
		transform: translateY(-2px);
		background: var(--blue-deep);
		box-shadow: 0 20px 34px -16px rgba(37, 99, 235, 0.75);
	}

	.btn-primary:focus-visible {
		outline: 3px solid rgba(37, 99, 235, 0.4);
		outline-offset: 2px;
	}

	.btn-ghost {
		background: var(--surface);
		color: var(--ink);
		border-color: var(--border-strong);
	}

	.btn-ghost:hover {
		transform: translateY(-2px);
		border-color: var(--blue);
		color: var(--blue-deep);
	}

	.btn-ghost:focus-visible {
		outline: 3px solid rgba(30, 58, 95, 0.25);
		outline-offset: 2px;
	}

	.hero-note {
		margin-top: 1.6rem;
		font-size: 0.95rem;
		color: var(--ink-muted);
	}

	.caps {
		padding: clamp(3.5rem, 7vw, 6rem) 1.5rem;
		background: var(--surface);
		border-top: 1px solid var(--border);
		border-bottom: 1px solid var(--border);
	}

	.caps-inner {
		max-width: 1140px;
		margin: 0 auto;
	}

	.block-header {
		text-align: center;
		margin-bottom: 2.75rem;
	}

	.block-eyebrow {
		display: inline-block;
		font-size: 0.85rem;
		font-weight: 700;
		letter-spacing: 0.12em;
		text-transform: uppercase;
		color: var(--blue-deep);
		margin-bottom: 0.7rem;
	}

	.block-heading {
		font-size: clamp(1.85rem, 3vw, 2.7rem);
		font-weight: 700;
		letter-spacing: -0.01em;
		color: var(--ink);
		margin: 0;
	}

	.caps-grid {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 1.5rem;
	}

	.cap-card {
		display: flex;
		flex-direction: column;
		height: 100%;
		padding: 2rem 1.75rem;
		border: 1px solid var(--border);
		border-radius: 14px;
		background: var(--bg);
		transition: transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;
	}

	.cap-card:hover {
		transform: translateY(-4px);
		border-color: var(--blue);
		box-shadow: 0 24px 44px -30px rgba(30, 58, 95, 0.5);
	}

	.cap-command {
		font-size: 0.95rem;
		color: var(--ink-soft);
		margin-bottom: 1rem;
	}

	.cap-command-prompt {
		color: var(--blue);
		font-weight: 700;
		margin-right: 0.4rem;
	}

	.cap-title {
		font-size: 1.3rem;
		font-weight: 700;
		color: var(--ink);
		margin: 0 0 0.7rem;
	}

	.cap-description {
		font-size: 1.02rem;
		line-height: 1.65;
		color: var(--ink-soft);
		margin: 0 0 1.5rem;
	}

	.cap-status {
		display: inline-flex;
		align-items: center;
		gap: 0.45rem;
		margin-top: auto;
		align-self: flex-start;
		font-size: 0.85rem;
		font-weight: 600;
		color: var(--blue-deep);
	}

	.cap-status-dot {
		width: 8px;
		height: 8px;
		border-radius: 50%;
		background: var(--blue);
	}

	.cta {
		padding: clamp(3.5rem, 6vw, 5.5rem) 1.5rem;
		background: var(--bg);
	}

	.cta-inner {
		max-width: 940px;
		margin: 0 auto;
	}

	.palette {
		border: 1px solid var(--border);
		border-radius: 16px;
		background: var(--surface);
		overflow: hidden;
		box-shadow: 0 36px 70px -40px rgba(30, 58, 95, 0.5);
	}

	.palette-bar {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 1rem;
		padding: 1rem 1.25rem;
		background: var(--gutter);
		border-bottom: 1px solid var(--border);
	}

	.palette-search {
		display: inline-flex;
		align-items: center;
		gap: 0.6rem;
		font-size: 1rem;
		color: var(--ink-muted);
	}

	.palette-search svg {
		width: 18px;
		height: 18px;
		color: var(--blue);
	}

	.palette-key {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		min-width: 28px;
		height: 28px;
		padding: 0 0.5rem;
		border-radius: 6px;
		border: 1px solid var(--border-strong);
		background: var(--surface);
		font-size: 0.85rem;
		color: var(--ink-soft);
	}

	.palette-body {
		padding: clamp(2.25rem, 4vw, 3.25rem) clamp(1.5rem, 4vw, 3rem);
		text-align: center;
	}

	.cta-title {
		font-size: clamp(1.7rem, 3vw, 2.5rem);
		font-weight: 700;
		letter-spacing: -0.01em;
		margin: 0 auto 1rem;
		max-width: 22ch;
		color: var(--ink);
	}

	.cta-sub {
		max-width: 56ch;
		margin: 0 auto 2rem;
		font-size: clamp(1.02rem, 1.3vw, 1.18rem);
		line-height: 1.7;
		color: var(--ink-soft);
	}

	.cta-actions {
		display: flex;
		gap: 1rem;
		justify-content: center;
		flex-wrap: wrap;
		margin-bottom: 1.5rem;
	}

	.cta-offer {
		font-size: 1rem;
		color: var(--ink-soft);
		margin: 0;
	}

	.cta-offer strong { color: var(--blue-deep); }

	.testimonials {
		padding: clamp(3.5rem, 7vw, 6rem) 1.5rem;
		background: var(--surface);
		border-top: 1px solid var(--border);
	}

	.testimonials-inner {
		max-width: 1240px;
		margin: 0 auto;
	}

	.testimonials-carousel {
		margin-top: 1rem;
	}

	.testimonial-card {
		display: flex;
		flex-direction: column;
		height: 100%;
		padding: 1.75rem;
		border: 1px solid var(--border);
		border-radius: 14px;
		background: var(--bg);
		margin: 0;
		box-sizing: border-box;
	}

	.testimonial-meta {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 1rem;
		font-size: 0.78rem;
		color: var(--ink-muted);
	}

	.testimonial-index {
		color: var(--blue-deep);
		font-weight: 700;
	}

	.testimonial-file { letter-spacing: 0.04em; }

	.testimonial-quote {
		flex: 1;
		font-size: 1rem;
		line-height: 1.7;
		color: var(--ink-soft);
		margin: 0 0 1.25rem;
	}

	.testimonial-author {
		font-size: 0.95rem;
		font-weight: 600;
		color: var(--ink);
	}

	.testimonial-author-prompt {
		color: var(--blue);
		margin-right: 0.4rem;
	}

	.modal-overlay {
		--bg: #eef2f9;
		--surface: #ffffff;
		--gutter: #f1f5fb;
		--border: #dbe4f0;
		--border-strong: #c6d4e6;
		--ink: #1e293b;
		--ink-soft: #475569;
		--ink-muted: #8190a5;
		--blue: #2563eb;
		--blue-deep: #1d4ed8;
		--blue-soft: #dbe8fe;
		position: fixed;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 1.5rem;
		background: rgba(30, 41, 59, 0.5);
		backdrop-filter: blur(4px);
		-webkit-backdrop-filter: blur(4px);
		cursor: pointer;
		z-index: 100000;
		pointer-events: auto;
	}

	.modal-dialog {
		position: relative;
		pointer-events: auto;
		width: 100%;
		max-width: 500px;
		max-height: calc(100vh - 3rem);
		max-height: calc(100dvh - 3rem);
		overflow: hidden;
		border-radius: 16px;
		background: var(--surface);
		border: 1px solid var(--border);
		box-shadow: 0 40px 90px -30px rgba(30, 58, 95, 0.55);
		cursor: default;
	}

	.modal-scroll {
		max-height: calc(100vh - 3rem);
		max-height: calc(100dvh - 3rem);
		overflow-y: auto;
		-webkit-overflow-scrolling: touch;
		padding: clamp(2.25rem, 4vw, 2.75rem) clamp(1.75rem, 3vw, 2.25rem);
	}

	.modal-close {
		position: absolute;
		top: 1rem;
		right: 1.1rem;
		width: 40px;
		height: 40px;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 1.9rem;
		line-height: 1;
		border-radius: 10px;
		border: 1px solid var(--border);
		background: var(--gutter);
		color: var(--ink-muted);
		cursor: pointer;
		transition: background 0.2s ease, color 0.2s ease;
		z-index: 1;
	}

	.modal-close:hover {
		background: var(--blue-soft);
		color: var(--blue-deep);
	}

	.modal-close svg {
		width: 18px;
		height: 18px;
	}

	.modal-badge {
		display: inline-block;
		padding: 0.4rem 0.9rem;
		border-radius: 999px;
		background: var(--blue-soft);
		color: var(--blue-deep);
		font-size: 0.82rem;
		font-weight: 700;
		letter-spacing: 0.04em;
		margin-bottom: 0.9rem;
	}

	.btn-block {
		width: 100%;
	}

	.modal-title {
		font-size: 1.6rem;
		font-weight: 700;
		margin: 0 0 0.5rem;
		color: var(--ink);
	}

	.modal-description {
		font-size: 1rem;
		line-height: 1.6;
		color: var(--ink-soft);
		margin: 0 0 1.5rem;
	}

	.modal-field {
		margin-bottom: 1.1rem;
		text-align: left;
	}

	.modal-field label {
		display: block;
		font-size: 0.95rem;
		font-weight: 600;
		color: var(--ink);
		margin-bottom: 0.45rem;
	}

	.modal-field input,
	.modal-field textarea {
		width: 100%;
		padding: 0.8rem 0.95rem;
		border-radius: 10px;
		border: 1px solid var(--border-strong);
		background: var(--bg);
		font-family: inherit;
		font-size: 1rem;
		color: var(--ink);
		transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
		box-sizing: border-box;
	}

	.modal-field textarea { resize: vertical; }

	.modal-field input:focus,
	.modal-field textarea:focus {
		outline: none;
		background: var(--surface);
		border-color: var(--blue);
		box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.18);
	}

	.modal-field input:disabled,
	.modal-field textarea:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}

	.modal-actions {
		display: flex;
		gap: 0.75rem;
		justify-content: flex-end;
		margin-top: 1.6rem;
	}

	.btn:disabled {
		opacity: 0.65;
		cursor: not-allowed;
		transform: none;
		box-shadow: none;
	}

	.btn-spinner {
		width: 1.05em;
		height: 1.05em;
		flex-shrink: 0;
		margin-right: 0.55rem;
		border: 2px solid rgba(255, 255, 255, 0.45);
		border-top-color: #ffffff;
		border-radius: 50%;
		-webkit-animation: btn-spin 0.7s linear infinite;
		animation: btn-spin 0.7s linear infinite;
	}

	@-webkit-keyframes btn-spin {
		to { -webkit-transform: rotate(360deg); }
	}

	@keyframes btn-spin {
		to { transform: rotate(360deg); }
	}

	.modal-success {
		text-align: center;
		padding: 0.75rem 0.25rem 0.5rem;
	}

	.success-check {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 72px;
		height: 72px;
		border-radius: 50%;
		background: #dcfce7;
		color: #16a34a;
		margin-bottom: 1.35rem;
	}

	.success-check svg {
		width: 36px;
		height: 36px;
	}

	.modal-feedback {
		margin: 0 0 1.2rem;
		padding: 0.85rem 1rem;
		border-radius: 12px;
		font-size: 0.98rem;
		font-weight: 500;
		text-align: left;
	}

	.modal-feedback-error {
		background: #fbe9ec;
		color: #9c1f2e;
	}

	.modal-feedback-title {
		margin: 0 0 0.4rem;
		font-weight: 700;
	}

	.modal-feedback-list {
		margin: 0;
		padding-left: 1.2rem;
	}

	.modal-feedback-list li {
		margin-bottom: 0.2rem;
	}

	.modal-fade-enter-active,
	.modal-fade-leave-active { transition: opacity 0.25s ease; }

	.modal-fade-enter-active .modal-dialog,
	.modal-fade-leave-active .modal-dialog { transition: transform 0.25s ease, opacity 0.25s ease; }

	.modal-fade-enter-from,
	.modal-fade-leave-to { opacity: 0; }

	.modal-fade-enter-from .modal-dialog,
	.modal-fade-leave-to .modal-dialog { transform: translateY(14px) scale(0.98); opacity: 0; }

	@media (prefers-reduced-motion: reduce) {
		.code-caret-blink { animation: none; }
		.tag-celebrate { animation: none; }
		.tag-celebrate::after { animation: none; }
		.tag-check { transition: none; }
	}

	@media (max-width: 992px) {
		.caps-grid { grid-template-columns: 1fr; }
	}

	@media (max-width: 640px) {
		.hero-actions,
		.cta-actions { flex-direction: column; align-items: stretch; }

		.btn { width: 100%; }

		.editor-path { display: none; }

		.editor-tabs {
			flex-wrap: nowrap;
			overflow-x: auto;
			gap: 0.3rem;
		}

		.editor-tab {
			padding: 0.35rem 0.6rem;
			font-size: 0.76rem;
		}

		.editor-body {
			padding: 1rem 0;
		}

		.editor-gutter {
			padding: 0 0.65rem;
		}

		.editor-code {
			padding: 0 0.85rem;
		}

		.code-headline {
			font-size: clamp(1.3rem, 6vw, 1.8rem);
		}

		.modal-actions { flex-direction: column-reverse; }

		.modal-actions .btn { width: 100%; }
	}
</style>